import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, SelectInput, TextInput } from "@pericles/framework-frontend";

import {
  assignRoadmapSegmentUseCase,
  createRoadmapSegment,
  deleteRoadmapSegment,
  fetchLatestSegmentationStrategy,
  fetchRoadmapData,
  fetchRoadmapSegments,
  removeRoadmapSegmentUseCase,
  runRoadmapApplicationsAnalysis,
  updateRoadmapSegment,
  type RoadmapApplicationsAnalysis,
  type RoadmapSegmentationStrategyPlan,
  type RoadmapSegmentRecord,
  type RoadmapUseCaseRecord,
} from "../../api/roadmapClient";

import { InlineAlert } from "../InlineAlert";
import { SegmentConversationPanel } from "./SegmentConversationPanel";

const EMPTY_SEGMENT_FORM = {
  name: "",
  purpose: "",
  solutionType: "",
  aiWhatItDoes: "",
  impact: "",
};

function sortByName(a: { id: string; name?: string | null }, b: { id: string; name?: string | null }): number {
  return (a.name ?? "").localeCompare(b.name ?? "") || a.id.localeCompare(b.id);
}

export function RoadmapApplicationsPanel({ projectId }: { projectId: string }): JSX.Element {
  const [segments, setSegments] = useState<RoadmapSegmentRecord[]>([]);
  const [useCases, setUseCases] = useState<RoadmapUseCaseRecord[]>([]);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [isSegmentsLoading, setIsSegmentsLoading] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>("");
  const [segmentForm, setSegmentForm] = useState(EMPTY_SEGMENT_FORM);
  const [newSegmentForm, setNewSegmentForm] = useState(EMPTY_SEGMENT_FORM);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisInfo, setAnalysisInfo] = useState<RoadmapApplicationsAnalysis | null>(null);
  const [activeSegmentationPlan, setActiveSegmentationPlan] = useState<RoadmapSegmentationStrategyPlan | null>(null);
  const [analysisSettings, setAnalysisSettings] = useState({
    targetMin: 10,
    targetMax: 25,
    minUseCasesPerApp: 2,
    preferProcessGrouping: true,
  });

  const sortedUseCases = useMemo(() => [...useCases].sort(sortByName), [useCases]);
  const leafSegments = useMemo(() => segments.filter((segment) => segment.kind !== "PLATFORM"), [segments]);

  const segmentationRunSummary = useMemo(() => {
    const sizes = leafSegments.map((segment) => (segment.useCaseIds ?? []).length).sort((a, b) => a - b);
    const median =
      sizes.length === 0
        ? 0
        : sizes.length % 2 === 0
          ? (sizes[sizes.length / 2 - 1] + sizes[sizes.length / 2]) / 2
          : sizes[Math.floor(sizes.length / 2)];
    const unknownCount = leafSegments.filter((segment) =>
      (segment.name ?? "").toLowerCase().startsWith("unclassified"),
    ).length;
    const unknownPct = leafSegments.length ? Math.round((unknownCount / leafSegments.length) * 10000) / 100 : 0;
    return {
      segmentCount: leafSegments.length,
      medianClusterSize: median,
      unknownPct,
    };
  }, [leafSegments]);

  const useCaseNameById = useMemo(() => {
    return new Map(useCases.map((useCase) => [useCase.id, useCase.name ?? useCase.id] as const));
  }, [useCases]);

  const useCaseSegmentMap = useMemo(() => {
    const map = new Map<string, string>();
    segments.forEach((segment) => {
      (segment.useCaseIds ?? []).forEach((useCaseId) => {
        map.set(useCaseId, segment.id);
      });
    });
    return map;
  }, [segments]);

  useEffect(() => {
    if (!projectId) return;
    let isActive = true;
    setIsSegmentsLoading(true);
    fetchRoadmapSegments(projectId)
      .then((loaded) => {
        if (!isActive) return;
        setSegments(loaded);
        setSelectedSegmentId((current) => {
          if (current && loaded.some((segment) => segment.id === current)) {
            return current;
          }
          return loaded[0]?.id ?? "";
        });
        setSegmentError(null);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setSegmentError("Impossible de charger les applications.");
      })
      .finally(() => {
        if (isActive) setIsSegmentsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let isActive = true;
    fetchRoadmapData(projectId)
      .then((data) => {
        if (!isActive) return;
        setUseCases(data.useCases ?? []);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setUseCases([]);
      });
    return () => {
      isActive = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let isActive = true;
    fetchLatestSegmentationStrategy(projectId)
      .then((plan) => {
        if (!isActive) return;
        if (plan?.status === "APPROVED" && plan.rulesetVersion === "seg-v2") {
          setActiveSegmentationPlan(plan);
          return;
        }
        setActiveSegmentationPlan(null);
      })
      .catch(() => {
        if (!isActive) return;
        setActiveSegmentationPlan(null);
      });
    return () => {
      isActive = false;
    };
  }, [projectId, segments.length]);

  useEffect(() => {
    const selectedSegment = segments.find((segment) => segment.id === selectedSegmentId);
    if (!selectedSegment) {
      setSegmentForm(EMPTY_SEGMENT_FORM);
      return;
    }
    setSegmentForm({
      name: selectedSegment.name ?? "",
      purpose: selectedSegment.purpose ?? "",
      solutionType: selectedSegment.solutionType ?? "",
      aiWhatItDoes: selectedSegment.aiWhatItDoes ?? "",
      impact: selectedSegment.impact ?? "",
    });
  }, [segments, selectedSegmentId]);

  const handleAddSegment = useCallback(async () => {
    const name = newSegmentForm.name.trim();
    if (!name) {
      setSegmentError("Le nom de l'application est requis.");
      return;
    }
    try {
      const segment = await createRoadmapSegment(projectId, {
        name,
        purpose: newSegmentForm.purpose.trim() || undefined,
        solutionType: newSegmentForm.solutionType.trim() || undefined,
        aiWhatItDoes: newSegmentForm.aiWhatItDoes.trim() || undefined,
        impact: newSegmentForm.impact.trim() || undefined,
      });
      setSegments((prev) => [...prev, segment]);
      setSelectedSegmentId(segment.id);
      setNewSegmentForm(EMPTY_SEGMENT_FORM);
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de créer l'application.");
    }
  }, [newSegmentForm, projectId]);

  const handleUpdateSegment = useCallback(async () => {
    const name = segmentForm.name.trim();
    if (!selectedSegmentId || !name) {
      setSegmentError("Sélectionnez une application et renseignez un nom.");
      return;
    }
    try {
      const segment = await updateRoadmapSegment(selectedSegmentId, {
        name,
        purpose: segmentForm.purpose.trim() || undefined,
        solutionType: segmentForm.solutionType.trim() || undefined,
        aiWhatItDoes: segmentForm.aiWhatItDoes.trim() || undefined,
        impact: segmentForm.impact.trim() || undefined,
      });
      setSegments((prev) => prev.map((item) => (item.id === segment.id ? segment : item)));
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de mettre à jour l'application.");
    }
  }, [segmentForm, selectedSegmentId]);

  const handleDeleteSegment = useCallback(async (segmentId: string) => {
    try {
      await deleteRoadmapSegment(segmentId);
      setSegments((prev) => prev.filter((segment) => segment.id !== segmentId));
      setSelectedSegmentId((current) => (current === segmentId ? "" : current));
      setSegmentError(null);
    } catch (error) {
      console.error(error);
      setSegmentError("Impossible de supprimer l'application.");
    }
  }, []);

  const handleAssignUseCase = useCallback(
    async (useCaseId: string, nextSegmentId: string) => {
      const currentSegmentId = useCaseSegmentMap.get(useCaseId) ?? "";
      if (currentSegmentId === nextSegmentId) return;
      try {
        if (nextSegmentId) {
          await assignRoadmapSegmentUseCase(nextSegmentId, useCaseId);
        } else if (currentSegmentId) {
          await removeRoadmapSegmentUseCase(currentSegmentId, useCaseId);
        }
        setSegments((prev) =>
          prev.map((segment) => {
            const useCaseIds = segment.useCaseIds ?? [];
            if (segment.id === nextSegmentId) {
              return {
                ...segment,
                useCaseIds: [...new Set([...useCaseIds, useCaseId])],
              };
            }
            if (segment.id === currentSegmentId) {
              return {
                ...segment,
                useCaseIds: useCaseIds.filter((id) => id !== useCaseId),
              };
            }
            return segment;
          }),
        );
        setSegmentError(null);
      } catch (error) {
        console.error(error);
        setSegmentError("Impossible de mettre à jour l'affectation du use case.");
      }
    },
    [useCaseSegmentMap],
  );

  const handleRunAiAnalysis = useCallback(async () => {
    setAnalysisRunning(true);
    setAnalysisError(null);
    try {
      const result = await runRoadmapApplicationsAnalysis(projectId, analysisSettings);
      setAnalysisInfo(result.analysis ?? null);
      if (!result.ok && result.reasons?.length) {
        setAnalysisError(result.reasons.map((reason) => reason.message).join(" "));
      }
      const refreshed = await fetchRoadmapSegments(projectId);
      setSegments(refreshed);
      setSelectedSegmentId(refreshed[0]?.id ?? "");
    } catch (error) {
      console.error(error);
      setAnalysisError("Impossible de lancer l'analyse IA.");
    } finally {
      setAnalysisRunning(false);
    }
  }, [analysisSettings, projectId]);

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-700">Applications</div>
            <p className="text-sm text-slate-600">
              Analysez les processus et use cases pour segmenter les applications, puis ajustez-les manuellement.
            </p>
            {activeSegmentationPlan ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                  Segmentation v2 active (planVersion {activeSegmentationPlan.planVersion}, ruleset {activeSegmentationPlan.rulesetVersion})
                </div>
                {activeSegmentationPlan.status === "APPROVED" && activeSegmentationPlan.stopReason === "NEEDS_APPROVAL" ? (
                  <div className="inline-flex rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                    Approved with warnings
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded border border-slate-200 p-2 text-xs text-slate-600">
              <div className="text-xs font-semibold uppercase text-slate-500">Settings</div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  Target min
                  <TextInput
                    type="number"
                    value={String(analysisSettings.targetMin)}
                    onChange={(event) =>
                      setAnalysisSettings((prev) => ({
                        ...prev,
                        targetMin: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  Target max
                  <TextInput
                    type="number"
                    value={String(analysisSettings.targetMax)}
                    onChange={(event) =>
                      setAnalysisSettings((prev) => ({
                        ...prev,
                        targetMax: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  Min use cases/app
                  <TextInput
                    type="number"
                    value={String(analysisSettings.minUseCasesPerApp)}
                    onChange={(event) =>
                      setAnalysisSettings((prev) => ({
                        ...prev,
                        minUseCasesPerApp: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="flex items-center gap-2 pt-5 text-xs">
                  <input
                    type="checkbox"
                    checked={analysisSettings.preferProcessGrouping}
                    onChange={(event) =>
                      setAnalysisSettings((prev) => ({
                        ...prev,
                        preferProcessGrouping: event.target.checked,
                      }))
                    }
                  />
                  Préférer le regroupement par process
                </label>
              </div>
            </div>
            <button
              type="button"
              className="button button-primary button-sm"
              onClick={() => void handleRunAiAnalysis()}
              disabled={analysisRunning || Boolean(activeSegmentationPlan)}
            >
              {activeSegmentationPlan ? "Legacy AI analysis" : analysisRunning ? "Analyse IA..." : "AI analysis"}
            </button>
          </div>
        </div>
        {activeSegmentationPlan ? (
          <InlineAlert tone="warning" title="Legacy AI analysis désactivée" description="Segmentation v2 est approuvée pour ce projet. Activez FEATURE_LEGACY_SEGMENTER=true pour réactiver l'ancien moteur." />
        ) : null}
        {activeSegmentationPlan ? (
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <div className="font-semibold text-slate-700">Segmentation v2 summary</div>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              <div>Segments: {segmentationRunSummary.segmentCount}</div>
              <div>Median cluster size: {segmentationRunSummary.medianClusterSize}</div>
              <div>Unclassified segments: {segmentationRunSummary.unknownPct}%</div>
            </div>
          </div>
        ) : null}
        {analysisError ? (
          <InlineAlert tone="danger" title="AI analysis" description={analysisError} />
        ) : null}
        {analysisInfo ? (
          <div className="rounded border border-slate-200 p-3 text-xs text-slate-500">
            <div>
              Dernière analyse IA • {analysisInfo.createdAt} • Stop reason: {analysisInfo.stopReason}
            </div>
            <div className="mt-2 grid gap-1 md:grid-cols-2">
              <div>Use cases: {analysisInfo.summary?.useCaseCount ?? 0}</div>
              <div>Applications: {analysisInfo.summary?.applicationCount ?? 0}</div>
              <div>
                Taille apps (min/med/max): {analysisInfo.summary?.minUseCasesPerApp ?? 0}/
                {analysisInfo.summary?.medianUseCasesPerApp ?? 0}/{analysisInfo.summary?.maxUseCasesPerApp ?? 0}
              </div>
              <div>Logique: {analysisInfo.summary?.explanation ?? "—"}</div>
            </div>
          </div>
        ) : null}
        {segmentError ? <InlineAlert tone="danger" title="Applications" description={segmentError} /> : null}
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold text-slate-700">Applications existantes</div>
            {isSegmentsLoading ? (
              <div className="text-xs text-slate-500">Chargement des applications...</div>
            ) : segments.length ? (
              <ul className="space-y-2 text-sm">
                {leafSegments.map((segment) => (
                  <li
                    key={segment.id}
                    className={`flex items-center justify-between gap-2 rounded border px-3 py-2 ${
                      segment.id === selectedSegmentId ? "border-slate-400 bg-slate-50" : "border-slate-200"
                    }`}
                  >
                    <div>
                      <button
                        type="button"
                        className="text-left text-sm font-semibold text-slate-700"
                        onClick={() => setSelectedSegmentId(segment.id)}
                      >
                        {segment.name}{segment.kind === "PLATFORM" ? " (Platform)" : ""}
                      </button>
                      <div className="text-[11px] text-slate-500">
                        {(segment.useCaseIds ?? [])
                          .slice(0, 3)
                          .map((id) => useCaseNameById.get(id) ?? id)
                          .join(" • ") || "No linked use cases"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{(segment.useCaseIds ?? []).length} use case(s)</span>
                      {segment.name?.toLowerCase().startsWith("unclassified") ? (
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                          Missing domain metadata
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="button button-secondary button-sm"
                        onClick={() => void handleDeleteSegment(segment.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">Aucune application pour ce projet.</div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold text-slate-700">Créer une application</div>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Nom
              <TextInput
                value={newSegmentForm.name}
                onChange={(event) => setNewSegmentForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Ex: Automatisation RH"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Purpose
              <TextInput
                value={newSegmentForm.purpose}
                onChange={(event) => setNewSegmentForm((prev) => ({ ...prev, purpose: event.target.value }))}
                placeholder="Objectif de l'application"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Solution type
              <TextInput
                value={newSegmentForm.solutionType}
                onChange={(event) => setNewSegmentForm((prev) => ({ ...prev, solutionType: event.target.value }))}
                placeholder="Type de solution"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              AI what it does
              <TextInput
                value={newSegmentForm.aiWhatItDoes}
                onChange={(event) => setNewSegmentForm((prev) => ({ ...prev, aiWhatItDoes: event.target.value }))}
                placeholder="Ce que l'IA fait"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Impact
              <TextInput
                value={newSegmentForm.impact}
                onChange={(event) => setNewSegmentForm((prev) => ({ ...prev, impact: event.target.value }))}
                placeholder="Impact attendu"
              />
            </label>
            <button type="button" className="button button-primary button-sm self-start" onClick={handleAddSegment}>
              Ajouter l'application
            </button>
          </div>
        </div>

        <div className="rounded border border-slate-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-700">Détails de l'application</div>
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={handleUpdateSegment}
              disabled={!selectedSegmentId}
            >
              Enregistrer les modifications
            </button>
          </div>
          {selectedSegmentId ? (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Nom
                <TextInput
                  value={segmentForm.name}
                  onChange={(event) => setSegmentForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Purpose
                <TextInput
                  value={segmentForm.purpose}
                  onChange={(event) => setSegmentForm((prev) => ({ ...prev, purpose: event.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Solution type
                <TextInput
                  value={segmentForm.solutionType}
                  onChange={(event) => setSegmentForm((prev) => ({ ...prev, solutionType: event.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                AI what it does
                <TextInput
                  value={segmentForm.aiWhatItDoes}
                  onChange={(event) => setSegmentForm((prev) => ({ ...prev, aiWhatItDoes: event.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Impact
                <TextInput
                  value={segmentForm.impact}
                  onChange={(event) => setSegmentForm((prev) => ({ ...prev, impact: event.target.value }))}
                />
              </label>
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500">Sélectionnez une application pour l'éditer.</div>
          )}
        </div>

        <div className="rounded border border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-700">Assistant application</div>
          {selectedSegmentId ? (
            <div className="mt-3">
              <SegmentConversationPanel segmentId={selectedSegmentId} />
            </div>
          ) : (
            <div className="mt-2 text-xs text-slate-500">Sélectionnez une application pour démarrer le chat.</div>
          )}
        </div>

        <div className="rounded border border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-700">Affectation des use cases</div>
          {sortedUseCases.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left">Use case</th>
                    <th className="px-2 py-2 text-left">Application</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sortedUseCases.map((useCase) => (
                    <tr key={useCase.id}>
                      <td className="px-2 py-2 text-slate-700">{useCase.name ?? useCase.id}</td>
                      <td className="px-2 py-2">
                        <SelectInput
                          value={useCaseSegmentMap.get(useCase.id) ?? ""}
                          onChange={(event) => void handleAssignUseCase(useCase.id, event.target.value)}
                        >
                          <option value="">Non affecté</option>
                          {leafSegments.map((segment) => (
                            <option key={segment.id} value={segment.id}>
                              {segment.name}{segment.kind === "PLATFORM" ? " (Platform)" : ""}
                            </option>
                          ))}
                        </SelectInput>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500">Aucun use case chargé pour ce projet.</div>
          )}
        </div>
      </div>
    </Card>
  );
}
